import {
  createFileRoute,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import { requireFeature } from "@/lib/feature-gate";

export const Route = createFileRoute("/user/_layout/leave")({
  beforeLoad: () => requireFeature("leave"),
  component: LeaveRequestPage,
});

import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import { Plus } from "lucide-react";
import useFetchLeaveBalances from "@/hooks/api/leave/user-leave-balances/useFetchLeaveBalances";
import useFetchLeaveRequests from "@/hooks/api/leave/leave-request/useFetchLeaveRequests";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { EmployeeBalance } from "@/types/leave";
import { RequestList } from "@/components/user/leave-request/request-list";
import { LeaveRequestDetail } from "@/components/crud/forms/LeaveRequestDetail";
import { UserLeaveRequestDialog } from "@/components/user/leave-request/UserLeaveRequestDialog";
import useFetchLeaveRequestById from "@/hooks/api/leave/leave-request/useFetchLeaveRequestById";
import { BalanceHistoryList } from "@/components/user/leave-request/balance-history-list";
import { BalanceGrid } from "@/components/user/leave-request/balance-grid";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import { useAuthStore } from "@/stores/useAuthStore";
import { useOrgSettingsStore } from "@/stores/useOrgSettingsStore";
import useCancelLeaveRequest from "@/hooks/api/leave/leave-request/useCancelLeaveRequest";
import useFetchLeaveBalanceHistory from "@/hooks/api/leave/user-leave-balance-history/useFetchLeaveBalanceHistory";
import type { LeaveRequest } from "@/types/leave";
import { Card } from "@/components/ui/card";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";

function LeaveRequestPage() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [balanceSearch, setBalanceSearch] = useState("");

  // Deep-link from a notification: ?leaveRequestId=<id> opens a read-only
  // detail sheet for the employee's own request (fetched by id). When the org
  // routes leave through supervisors, that link instead forwards to the full
  // detail page, which also shows the approval chain the sheet can't render.
  const supervisorApprovalEnabled = useOrgSettingsStore(
    (s) => s.settings?.leave_supervisor_approval_required ?? false,
  );
  const deepLinkId = new URLSearchParams(location.searchStr).get("leaveRequestId");
  const deepLinkRequestId = deepLinkId ? Number(deepLinkId) : null;
  const [isDetailSheetOpen, setIsDetailSheetOpen] = useState(false);
  const deepLinkQuery = useFetchLeaveRequestById(deepLinkRequestId, false, {
    enabled: deepLinkRequestId != null && !supervisorApprovalEnabled,
  });
  useEffect(() => {
    if (deepLinkRequestId == null) return;
    if (supervisorApprovalEnabled) {
      navigate({
        to: "/user/leave/$id",
        params: { id: String(deepLinkRequestId) },
        replace: true,
      });
      return;
    }
    if (deepLinkQuery.data) {
      setIsDetailSheetOpen(true);
    } else if (deepLinkQuery.isError) {
      navigate({ to: "/user/leave", search: {} });
    }
  }, [
    deepLinkRequestId,
    supervisorApprovalEnabled,
    deepLinkQuery.data,
    deepLinkQuery.isError,
    navigate,
  ]);
  const handleCloseDetailSheet = () => {
    setIsDetailSheetOpen(false);
    if (deepLinkRequestId != null) {
      navigate({ to: "/user/leave", search: {} });
    }
  };

  const balancesQuery = useFetchLeaveBalances(
    {
      user: user?.user_id,
      page_size: 100,
      search: balanceSearch || undefined,
    },
    { enabled: user != null },
  );
  const balances = balancesQuery.data?.results ?? [];

  const {
    page: leaveRequestsPage,
    setPage: setLeaveRequestsPage,
    pageSize: leaveRequestsPageSize,
    handlePageSizeChange: handleLeaveRequestsPageSizeChange,
  } = usePaginationState();

  const leaveRequestsQuery = useFetchLeaveRequests(
    { page: leaveRequestsPage, page_size: leaveRequestsPageSize },
    false,
    { enabled: user != null },
  );
  const leaveRequests = leaveRequestsQuery.data?.results ?? [];
  const leaveRequestsCount = leaveRequestsQuery.data?.count ?? 0;

  const cancelLeaveRequest = useCancelLeaveRequest();

  const balanceHistoryQuery = useFetchLeaveBalanceHistory(
    { user: user?.user_id },
    { enabled: user != null },
  );
  const balanceHistory = balanceHistoryQuery.data?.results ?? [];

  const isCanceling = cancelLeaveRequest.isPending;

  // Apply/edit both go through the same dialog; `requestToEdit` picks the mode.
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [selectedBalance, setSelectedBalance] =
    useState<EmployeeBalance | null>(null);
  const [requestToEdit, setRequestToEdit] = useState<LeaveRequest | null>(null);

  function handleApplyClick(balance: EmployeeBalance) {
    setSelectedBalance(balance);
    setRequestToEdit(null);
    setApplyDialogOpen(true);
  }

  function openNewRequestDialog() {
    setSelectedBalance(null);
    setRequestToEdit(null);
    setApplyDialogOpen(true);
  }

  function handleEditClick(request: LeaveRequest) {
    setSelectedBalance(null);
    setRequestToEdit(request);
    setApplyDialogOpen(true);
  }

  const handleSearch = (query: string) => {
    setBalanceSearch(query);
  };

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="p-6 pt-0 pb-4">
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-foreground">Leave</h2>
              <p className="text-muted-foreground">
                Manage your time off requests
              </p>
            </div>
            <Button
              onClick={openNewRequestDialog}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
              disabled={!user || isCanceling}
            >
              <Plus className="h-4 w-4 mr-2" />
              Apply Leave
            </Button>
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <BalanceGrid
              balances={balances}
              onApplyClick={handleApplyClick}
              onSearchChange={handleSearch}
              isLoading={!user}
            />
            <div className="space-y-4">
              <Card className="p-6">
                <Tabs defaultValue="requests" className="w-full">
                  <TabsList className="p-0 bg-background justify-start border-b rounded-none mb-4">
                    <TabsTrigger
                      value="requests"
                      className="rounded-none bg-background h-full data-[state=active]:shadow-none border-b-2 border-transparent border-x-0 border-t-0 data-[state=active]:border-primary px-4 py-2"
                    >
                      Requests
                    </TabsTrigger>
                    <TabsTrigger
                      value="history"
                      className="rounded-none bg-background h-full data-[state=active]:shadow-none border-b-2 border-transparent border-x-0 border-t-0 data-[state=active]:border-primary px-4 py-2"
                    >
                      Balance History
                    </TabsTrigger>
                  </TabsList>
                  <TabsContent value="requests" className="mt-0">
                    <RequestList
                      requests={leaveRequests}
                      onCancelLeaveRequest={(id, payload) =>
                        cancelLeaveRequest.mutateAsync({
                          id,
                          remarks: payload.remarks,
                        })
                      }
                      onEditLeaveRequest={handleEditClick}
                      isLoading={isCanceling}
                    />
                    {leaveRequestsCount > leaveRequestsPageSize && (
                      <PaginationComponent
                        count={leaveRequestsCount}
                        page={leaveRequestsPage}
                        onPageChange={setLeaveRequestsPage}
                        isLoading={leaveRequestsQuery.isFetching}
                        pageSize={leaveRequestsPageSize}
                        onPageSizeChange={handleLeaveRequestsPageSizeChange}
                        pageSizeOptions={PAGE_SIZE_OPTIONS}
                      />
                    )}
                  </TabsContent>
                  <TabsContent value="history" className="mt-0">
                    <BalanceHistoryList history={balanceHistory} />
                  </TabsContent>
                </Tabs>
              </Card>
            </div>
          </div>
        </div>
        <LeaveRequestDetail
          isOpen={isDetailSheetOpen}
          onClose={handleCloseDetailSheet}
          isLoading={deepLinkQuery.isFetching}
          leaveRequest={deepLinkQuery.data ?? null}
          readOnly
        />
        <UserLeaveRequestDialog
          open={applyDialogOpen}
          onOpenChange={setApplyDialogOpen}
          request={requestToEdit}
          initialBalance={selectedBalance}
        />
      </div>
    </SidebarInset>
  );
}
